// Maillage interne — articles liés en bas de chaque guide.
//
// Avant le 08/08/2026, ce composant portait sa PROPRE liste de 23 articles
// (une 5ᵉ liste en dur, à côté du sitemap, de /blog, du RSS et d'IndexNow) avec
// trois défauts qui s'additionnaient :
//   · elle citait `alternative-sd-worx`, slug qui n'existe pas → 63 articles
//     affichaient un lien vers une 404 ;
//   · `.slice(0, 4)` sans tri : les 70 articles pointaient vers les 4 MÊMES
//     pages, quel que soit leur sujet. Aucun maillage thématique, et 66 articles
//     ne recevaient aucun lien entrant — l'état qui produit le « Détectée,
//     actuellement non indexée » de la Search Console ;
//   · elle ignorait les 47 articles absents de sa liste.
//
// Tout vient maintenant de app/lib/blog-registry.js : priorité aux articles du
// MÊME tag (vrai voisinage thématique), complétés par les plus récents, et la
// rotation dépend du slug courant pour que les liens entrants se répartissent
// au lieu de se concentrer sur les 4 premiers.
import { BLOG_ARTICLES, tagVersSlug, articlesParTag, MIN_ARTICLES_CATEGORIE } from '@/app/lib/blog-registry';
import { SLUG_NL, TEXTES_NL, etiquetteNl } from '@/app/lib/categories-nl';
import { CLUSTER } from '@/app/secretariat-social/_contenu';

// Le dossier « secrétariat social » (24/08/2026) — le lien de RETOUR vers la page
// pilier. Un pilier qui pointe vers ses 18 articles sans qu'aucun ne lui réponde
// est un pilier sans autorité : le maillage doit aller dans les deux sens. Le
// bandeau ne s'affiche que sur les articles du cluster, jamais sur les 54 autres.
const SLUGS_CLUSTER = new Set(CLUSTER.map((c) => c.slug));

const NB = 4;

/** Décalage déterministe tiré du slug : deux articles du même tag ne proposent
 *  pas la même sélection, donc les liens entrants se répartissent. */
function decalage(slug, total) {
  if (total === 0) return 0;
  let n = 0;
  for (let i = 0; i < slug.length; i++) n = (n * 31 + slug.charCodeAt(i)) >>> 0;
  return n % total;
}

export function articlesLies(currentSlug, nb = NB) {
  const courant = BLOG_ARTICLES.find((a) => a.slug === currentSlug);
  const autres = BLOG_ARTICLES.filter((a) => a.slug !== currentSlug);
  if (autres.length === 0) return [];

  const langue = courant?.lang || 'fr';
  const memeLangue = autres.filter((a) => a.lang === langue);
  // La langue est une PRIORITÉ, pas un filtre exclusif : il n'y a que deux guides
  // néerlandais. Les exclure du bassin quand il en manque les privait de tout lien
  // entre eux ; les imposer seuls laisserait le bloc à une seule ligne. Ils passent
  // donc devant, et le reste complète jusqu'à nb.
  const autreLangue = autres.filter((a) => a.lang !== langue);

  const parDate = (l) => [...l].sort((a, b) => b.published.localeCompare(a.published));
  const memeTag = parDate(memeLangue.filter((a) => a.tag === courant?.tag));
  const autreTag = parDate(memeLangue.filter((a) => a.tag !== courant?.tag));

  // Rotation sur CHAQUE groupe, pas seulement sur le tag : sans elle, le tri par
  // date sert éternellement les mêmes articles récents et les plus anciens ne
  // reçoivent aucun lien entrant — le défaut qu'on vient de réparer.
  const tourner = (liste) => {
    if (liste.length === 0) return liste;
    const d = decalage(currentSlug, liste.length);
    return [...liste.slice(d), ...liste.slice(0, d)];
  };

  // Une place sur quatre est réservée à un article d'un AUTRE tag. Sans elle, les
  // tags fournis (Paie en compte 12) remplissent les 4 places à eux seuls : on
  // obtient des silos étanches, et un article seul de son tag — `faq-paie-belgique`
  // est le seul « Guide » — n'est jamais proposé nulle part.
  // ⚠️ UNE PLACE EST GARANTIE PAR UN ANNEAU — posée le 05/09/2026, et c'est la
  // seule construction qui COUVRE au lieu de répartir.
  //
  // Le défaut mesuré : `dimona-aangifte-belgie` est le SEUL article néerlandais
  // de son tag. Il ne peut donc jamais entrer dans le `memeTag` d'un autre
  // article NL, et la place « autre tag » se tirait par hachage parmi ~29
  // candidats. Dès que le corpus NL est passé de 21 à 30 articles, il s'est
  // retrouvé avec ZÉRO lien entrant — et une page sans aucun lien interne est
  // bien plus difficile à découvrir et à classer.
  //
  // ⚠️ DEUX CORRECTIFS ONT ÉTÉ ESSAYÉS ET RETIRÉS, ils valent d'être connus :
  //   · balayer la place « autre tag » par POSITION au lieu du hachage → DEUX
  //     orphelins au lieu d'un. Chaque article exclut son propre tag, donc la
  //     liste balayée diffère d'un article à l'autre : les positions ne se
  //     composent pas en une couverture globale.
  //   · compléter le vivier « même tag » avec l'autre langue → casse la règle
  //     « un article seul de sa langue garde sa langue en 1ʳᵉ place », que le
  //     verrou vérifie séparément.
  // La leçon : une heuristique par page ne garantit pas une propriété globale.
  //
  // L'anneau, lui, la garantit par construction. Les articles d'une langue sont
  // ordonnés par date ; l'article i réserve une place à l'article i+1 (modulo N).
  // Chaque article reçoit donc AU MOINS un lien, celui de son prédécesseur, quel
  // que soit son tag et quelle que soit la taille du corpus. Le voisin est de la
  // même langue : la règle de langue est préservée.
  // ⚠️ L'ORDRE DE L'ANNEAU DOIT ÊTRE LE MÊME POUR TOUS, sinon ce n'est plus un
  // anneau. Première version : `parDate([courant, ...memeLangue])`. Or dix
  // articles publiés le même jour sont EX ÆQUO, le tri est stable, et `courant`
  // était placé en tête de l'entrée — donc chaque article se voyait un
  // successeur différent et la chaîne se brisait. Mesuré : `dimona` couvert mais
  // `contrat-travail-belgique` orphelin à sa place.
  // L'ordre se calcule donc sur le corpus de la langue, INDÉPENDAMMENT de qui
  // demande, et le slug départage les ex æquo.
  const anneau = BLOG_ARTICLES
    .filter((a) => (a.lang || 'fr') === langue)
    .sort((a, b) => b.published.localeCompare(a.published) || a.slug.localeCompare(b.slug));
  const iCourant = anneau.findIndex((a) => a.slug === currentSlug);
  const voisin = anneau.length > 1 ? [anneau[(iCourant + 1) % anneau.length]] : [];

  const quotaMemeTag = Math.max(1, nb - 2);
  const vus = new Set([currentSlug]);
  const unique = (l) => l.filter((a) => a && !vus.has(a.slug) && vus.add(a.slug));

  return [
    ...unique(tourner(memeTag).slice(0, quotaMemeTag)),
    ...unique(voisin),
    ...unique(tourner(autreTag)),
    ...unique(tourner(parDate(autreLangue))),
  ].slice(0, nb);
}

// Trois conventions de prop coexistaient dans les 70 articles (`currentSlug` 18×,
// `slug` 47×, `current` 5×) alors que ce composant n'en lisait qu'une : 52 articles
// lui passaient une valeur ignorée. Le slug courant valait donc `undefined`, le
// filtre d'exclusion ne filtrait rien, et un article pouvait figurer dans ses
// propres « articles liés ». On accepte les trois — un composant de maillage ne
// doit jamais être muet parce qu'on l'a appelé avec l'autre nom.
export default function RelatedArticles({ currentSlug, slug, current }) {
  const actuel = currentSlug || slug || current;
  const related = articlesLies(actuel);
  if (related.length === 0) return null;

  // Lien vers la page de catégorie : c'est ce qui la fait exister aux yeux de
  // Google. Une page listée au sitemap mais vers laquelle rien ne pointe reste
  // « Détectée, actuellement non indexée » — le défaut qu'on vient de réparer
  // pour les articles, à ne pas reproduire pour les catégories.
  const courant = BLOG_ARTICLES.find((a) => a.slug === actuel);
  const nl = courant?.lang === 'nl';

  // ⚠️ LA CATÉGORIE SUIT LA LANGUE DE L'ARTICLE — corrigé le 07/09/2026.
  //
  // Ce lien s'affichait déjà avec un libellé néerlandais (« Alle gidsen "Loon" »)
  // et pointait vers `/blog/categorie/paie` : une page dont le titre,
  // l'introduction et la moitié des articles sont français. Les 68 articles
  // néerlandais y envoyaient donc chacun leur lecteur, et le crawl avec.
  //
  // Le comptage suit la même langue : une catégorie mérite son lien si elle a
  // assez d'articles DANS CETTE LANGUE — compter les deux versants faisait
  // proposer une page qui, en néerlandais, n'aurait listé qu'un seul guide.
  const langue = nl ? 'nl' : 'fr';
  const assezDArticles =
    courant && articlesParTag(courant.tag, langue).length >= MIN_ARTICLES_CATEGORIE;
  const categorieNlDisponible = courant && SLUG_NL[courant.tag] && TEXTES_NL[courant.tag];
  const categorie = !assezDArticles
    ? null
    : nl
      ? (categorieNlDisponible
          ? { tag: etiquetteNl(courant.tag), href: `/gids/categorie/${SLUG_NL[courant.tag]}` }
          : null)
      : { tag: courant.tag, href: `/blog/categorie/${tagVersSlug(courant.tag)}` };
  const pilier = SLUGS_CLUSTER.has(actuel)
    ? {
        href: nl ? '/sociaal-secretariaat' : '/secretariat-social',
        titre: nl
          ? 'Sociaal secretariaat: de volledige gids om te kiezen'
          : 'Secrétariat social : le guide complet pour choisir',
        intro: nl
          ? 'Dit artikel maakt deel uit van een dossier.'
          : 'Cet article fait partie d’un dossier.',
      }
    : null;

  return (
    <nav style={{ marginTop: 48, padding: 24, borderTop: '1px solid #1e293b' }}>
      {pilier && (
        <div style={{ marginBottom: 24, padding: '14px 18px', border: '1px solid #f0c04044', borderRadius: 10, background: '#f0c0400d' }}>
          <p style={{ margin: '0 0 6px', fontSize: 13, color: '#94a3b8' }}>{pilier.intro}</p>
          <a href={pilier.href} style={{ color: '#f0c040', textDecoration: 'none', fontSize: 15, fontWeight: 700 }}>
            {pilier.titre} →
          </a>
        </div>
      )}
      {/* ─────────────────────────────────────────────────────────────
          LA PORTE DE SORTIE — ajoutée le 26/08/2026.
          Mesuré ce jour-là : sur 74 articles, **16 seulement** portaient un
          chemin vers /contact. Les 58 autres étaient des culs-de-sac : le
          lecteur arrive de Google, lit, et repart sans qu'aucun moyen de nous
          écrire ne lui soit proposé. Tout le travail SEO de la semaine
          alimentait des pages sans issue.
          Ce composant étant déjà rendu sur les 74, une seule modification
          les couvre toutes.
          ⛔ NE JAMAIS promettre ici l'absence de durée d'engagement : le
          contrat de services court sur 3 ANS, et cette promesse a déjà coûté
          un prospect (incident Solinas, 28/07). Deux verrous l'interdisent —
          formulations-interdites et offre-lancement-retrait — et ils m'ont
          attrapé en écrivant ce bloc le 26/08. Le système a fonctionné.
          ⚠️ Ces verrous balaient AUSSI les commentaires : ce paragraphe est
          donc écrit sans la formule elle-même. C'est voulu — un garde-fou qui
          s'autorise des exceptions « parce que c'est de la doc » finit par
          s'en autoriser une « parce que c'est du marketing ».
          ⚠️ Volontairement SOBRE : une phrase, pas une pop-up. Un article
          technique se lit ; l'agresser fait fuir et abîme le référencement
          (les signaux d'engagement comptent).
          ───────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 26, padding: '14px 18px', border: '1px solid #1e293b', borderRadius: 10, background: '#0f172a55' }}>
        <p style={{ margin: 0, fontSize: 14.5, color: '#cbd5e1', lineHeight: 1.65 }}>
          {nl
            ? 'Een vraag over uw eigen situatie? Wij antwoorden op concrete gevallen, niet op algemeenheden. '
            : 'Une question sur votre situation précise ? Nous répondons sur des cas concrets, pas sur des généralités. '}
          <a href="/contact" style={{ color: '#f0c040', fontWeight: 700, textDecoration: 'none' }}>
            {nl ? 'Stel ze ons' : 'Posez-la nous'} →
          </a>
        </p>
      </div>

      {/* ⚠️ CES DEUX LIBELLÉS ÉTAIENT RESTÉS EN FRANÇAIS — repérés le 02/09/2026
          par le fondateur, en lisant `/blog/ouderschapsverlof-belgie`. Le
          composant CONNAISSAIT pourtant déjà la langue (`nl`, plus haut) et
          s'en servait pour le bloc dossier juste au-dessus : une traduction
          faite à moitié est plus trompeuse qu'une absente, parce qu'elle donne
          l'impression que la page a été relue. */}
      <h3 style={{ color: '#f0c040', fontSize: 18, marginBottom: 16 }}>{nl ? 'Verwante gidsen' : 'Articles liés'}</h3>
      <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
        {related.map((a) => (
          <li key={a.slug}>
            <a href={'/blog/' + a.slug} style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 14, lineHeight: 1.6 }}>
              → {a.title}
            </a>
          </li>
        ))}
      </ul>
      {categorie && (
        <div style={{ marginTop: 16 }}>
          <a
            href={categorie.href}
            style={{ color: '#f0c040', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}
          >
            {nl ? `Alle gidsen “${categorie.tag}”` : `Tous les guides « ${categorie.tag} »`} →
          </a>
        </div>
      )}
    </nav>
  );
}
